import type { INavItemBadgeType } from "@similarweb/ui-components";
import { EPrimaryItemType } from "@similarweb/ui-components";

import { isHidden, isLocked } from "app/common/services/pageClaims";
import { isPremiumFeatureAllowed } from "app/common/services/solutions2Helper";
import { swSettings } from "app/common/services/swSettings";
import { hasPremiumFeaturePermission } from "app/components/SideBar/utils";
import { AccountEducationComponent } from "app/components/sidebar3/primary-side-bar/educationAccount/AccountEducation";
import { OnboardingHub } from "app/components/sidebar3/primary-side-bar/secondary-actions/OnboardingHub/OnboardingHub";
import { UserSettings } from "app/components/sidebar3/primary-side-bar/secondary-actions/Settings/UserSettings";
import { sidebarTrackingDispatcher } from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteNavBarUtils";
import type { IPrimarySidebarItem } from "app/components/sidebar3/types";
import { ESidebarPlacement, SecondarySideBarType3 } from "app/components/sidebar3/types";
import { EPrimarySideBar3IdType, EPrimarySideBar3StateType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import { openHooksModalV5, openHooksModalV6 } from "app/services/ModalService";
import { EHooksVersions } from "app/services/SwHook";
import { isSI, isDMIAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import ActivationItem from "./primary-side-bar/activation/ActivationItem";
import { AfterTrialDigitalSuiteHomeItem } from "./primary-side-bar/afterTrialDigitalSuiteHome/AfterTrialDigitalSuiteHomeItem";
import { SimilarwebExtensionButton } from "./primary-side-bar/extension/SimilarwebExtensionButton";

const getUserSettingSidebarItemComponent = () =>
    function (isPrimarySidebarOpen, onClick) {
        return (
            <UserSettings
                key="userSettingsComponent"
                onClick={onClick}
                isPrimarySidebarOpen={isPrimarySidebarOpen}
            />
        );
    };

const getOnboardingHubSidebarItemComponent = () =>
    function (isPrimarySidebarOpen, onClick, onClose) {
        return (
            <OnboardingHub
                key="onboardingHub"
                onClick={onClick}
                onClose={onClose}
                isPrimarySidebarOpen={isPrimarySidebarOpen}
            />
        );
    };

export const onSubSectionClick = (
    state: EPrimarySideBar3StateType,
    id?: string,
    itemCounter?: number,
    isLocked?: boolean,
    badgeType?: INavItemBadgeType,
    ParentTrackName?: string,
) => {
    const trackingPrimaryItemProps = {
        depth: 1,
        type: "click",
        report: null,
        report_group: null,
        report_domain: state,
        analytics_domain: ParentTrackName,
        position_order: itemCounter,
        is_dropdown_list: false,
        is_locked: isLocked,
        tag: badgeType || "none",
    };
    sidebarTrackingDispatcher(trackingPrimaryItemProps);
    proNavigator.go(state);
};

export const onHookClick = (key, targetState, version?: string) => {
    const targetStateObj = proNavigator.getState(targetState);
    const targetLocation = LocationService.getLocationTracking(targetStateObj);
    switch (version) {
        case EHooksVersions.V6:
            openHooksModalV6(key, targetLocation);
            break;
        default:
            openHooksModalV5(key, targetLocation);
    }
};

export const dashboard = {
    title: "topbar.tabs.Dashboards",
    itemType: EPrimaryItemType.StandalonePrimary,
    icon: "sidebar3-dashboards" as const,
    trackName: "Dashboards",
    state: EPrimarySideBar3StateType.Dashboards,
    id: EPrimarySideBar3IdType.Dashboards,
    onClick: onSubSectionClick,
    get isLocked() {
        return isLocked(swSettings?.components?.Dashboard);
    },
    get isHidden() {
        return isHidden(swSettings?.components?.Dashboard);
    },
    onClickLocked: () =>
        onHookClick("dashboard", "dashboard", isDMIAfterTrial(swSettings) ? EHooksVersions.V6 : EHooksVersions.V5),
    placement: ESidebarPlacement.Top,
    menuPlacement: ESidebarPlacement.Top,
    secondaryBarType3: SecondarySideBarType3.Dashboards,
};

export const premiumFeatures = {
    title: "topbar.tabs.premiumfeatures.title",
    itemType: EPrimaryItemType.Premium,
    id: EPrimarySideBar3IdType.PremiumFeatures,
    icon: "spark" as const,
    trackName: "Premium Features",
    link: "/#/premium-features/home",
    state: EPrimarySideBar3StateType.PremiumFeatures,
    onClick: onSubSectionClick,
    get isLocked() {
        return !hasPremiumFeaturePermission();
    },
    get isHidden() {
        return !isPremiumFeatureAllowed();
    },
    placement: ESidebarPlacement.Top,
    menuPlacement: ESidebarPlacement.Bottom,
    secondaryBarType3: SecondarySideBarType3.PremiumFeatures,
};

export const upsellFeatures = {
    title: "topbar.tabs.si_premiumfeatures.title",
    itemType: EPrimaryItemType.Premium,
    id: EPrimarySideBar3IdType.SIPremiumFeatures,
    icon: "spark" as const,
    trackName: "Premium Features",
    link: "/#/premium-features",
    state: EPrimarySideBar3StateType.SIPremiumFeatures,
    onClick: onSubSectionClick,
    isLocked: false,
    get isHidden() {
        return !isSI(swSettings);
    },
    placement: ESidebarPlacement.Top,
    menuPlacement: ESidebarPlacement.Bottom,
    secondaryBarType3: SecondarySideBarType3.PremiumFeatures,
};

export const buyerJourney = {
    title: "topbar.tabs.buyerjourney.title",
    itemType: EPrimaryItemType.StandalonePrimary,
    id: EPrimarySideBar3IdType.ConversionAnalysis,
    icon: "sidebar3-conversion" as const,
    trackName: "Research/buyerJourney",
    link: "/#/conversion/home",
    state: EPrimarySideBar3StateType.Conversion_homepage,
    onClick: onSubSectionClick,
    onClickLocked: () => {
        onHookClick("conversionRate", "conversion-homepage", EHooksVersions.V5);
    },
    get isLocked() {
        return isLocked(swSettings?.components?.Conversion);
    },
    get isHidden() {
        return isHidden(swSettings?.components?.Conversion);
    },
    placement: ESidebarPlacement.Top,
    menuPlacement: ESidebarPlacement.Bottom,
    secondaryBarType3: SecondarySideBarType3.ConversionAnalysis,
};

export const whatsNew = {
    sidebarItemComponent: (isPrimarySidebarOpen: boolean) => (
        <AccountEducationComponent isPrimarySidebarOpen={isPrimarySidebarOpen} />
    ),
    title: "beamerComponent", //for the key
    itemType: EPrimaryItemType.SecondaryAction,
    get isHidden() {
        return !swSettings?.components?.Home?.resources?.HasWhatsNewFeature;
    },
    placement: ESidebarPlacement.Bottom,
    trackName: "Beamer",
};

export const similarwebExtention = {
    sidebarItemComponent: (isPrimarySidebarOpen: boolean) => (
        <SimilarwebExtensionButton isBarOpen={isPrimarySidebarOpen} />
    ),
    title: "similarwebExtensionComponent", //for the key
    itemType: EPrimaryItemType.SecondaryAction,
    placement: ESidebarPlacement.Bottom,
    trackName: "SimilarwebExtension",
};

// todo: temporary removed an onboarding hub, due to SI-7223. Used in app/components/sidebar3/dynamic-config/configs/sidebar31/getSalesItemsList.tsx
const onboardingHub = {
    itemType: EPrimaryItemType.SecondaryAction,
    id: EPrimarySideBar3IdType.Settings,
    title: "userSettingsComponent", //for the key
    sidebarItemComponent: getOnboardingHubSidebarItemComponent(),
    placement: ESidebarPlacement.Top,
    trackName: "Settings",
};

export const userSettings = {
    itemType: EPrimaryItemType.SecondaryAction,
    id: EPrimarySideBar3IdType.Settings,
    title: "userSettingsComponent", //for the key
    sidebarItemComponent: getUserSettingSidebarItemComponent(),
    placement: ESidebarPlacement.Bottom,
    trackName: "Settings",
};

export const activationHome = {
    title: "Homepage",
    itemType: EPrimaryItemType.SecondaryAction,
    icon: "homepage" as const,
    trackName: SecondarySideBarType3.Activation,
    state: EPrimarySideBar3StateType.Activation,
    id: EPrimarySideBar3IdType.Activation,
    get isHidden() {
        return swSettings.components?.Activation?.isDisabled;
    },
    onClick: onSubSectionClick,
    onClickLocked: () => {
        onHookClick("GetMoreResults", "Activation");
    },
    get isLocked() {
        return isLocked(swSettings.components?.Activation);
    },
    placement: ESidebarPlacement.Top,
    menuPlacement: ESidebarPlacement.Top,
    secondaryBarType3: SecondarySideBarType3.Activation,
    sidebarItemComponent: (isPrimarySidebarOpen: boolean) => (
        <ActivationItem
            key="activationComponentButton"
            isOpen={isPrimarySidebarOpen}
        />
    ),
};

export const getAfterTrialDigitalSuiteHomeSidebarItem = (isVisible: boolean): IPrimarySidebarItem => ({
    title: "digitalsuite.sidebar.home.title",
    itemType: EPrimaryItemType.SecondaryAction,
    icon: "homepage" as const,
    trackName: "AfterTrialDigitalSuiteHome",
    state: EPrimarySideBar3StateType.Home,
    id: EPrimarySideBar3IdType.Homepage,
    isHidden: !isVisible,
    placement: ESidebarPlacement.Top,
    menuPlacement: ESidebarPlacement.Top,
    secondaryBarType3: SecondarySideBarType3.None,
    sidebarItemComponent: (isPrimarySidebarOpen: boolean) => (
        <AfterTrialDigitalSuiteHomeItem
            key="afterTrialDigitalSuiteHomeItem"
            isOpen={isPrimarySidebarOpen}
        />
    ),
});
